// Servicio para el catálogo de insumos
// Este servicio se conecta al backend (InsumosController) para obtener datos en tiempo real
import axios from 'axios';

class InsumosService {
  constructor() {
    // El axios ya está configurado globalmente con la baseURL en main.js
    // URL base: http://172.16.1.36:9999/secah2015/api
  }

  /**
   * Buscar insumos para autocompletado
   * @param {string} texto - Texto a buscar (mínimo 2 caracteres)
   * @param {number} max - Número máximo de resultados (default: 10)
   * @param {string|number|null} area - Área solicitada (string ej. 'BIOMEDICA' o ID numérico)
   * @returns {Promise<Array>} Lista de insumos que coinciden
   */
  async buscarInsumos(texto, max = 10, area = null) {
    if (!texto || texto.length < 2) {
      return [];
    }

    try {
      // Construir parámetros
      const params = {
        q: texto,
        max: max
      };

      if (area !== null && area !== undefined && area !== '') {
        if (!Number.isNaN(Number(area)) && Number.isFinite(Number(area))) {
          params.areaId = Number(area);
        } else {
          params.area = area;
        }
      }

      // Llamar al endpoint usando axios global
      const response = await axios.get('/insumos/buscar', { params });

      if (response.data.success) {
        return response.data.resultados;
      } else {
        console.error('Error en la búsqueda:', response.data.message);
        return [];
      }
    } catch (error) {
      console.error('Error al buscar insumos:', error);
      return [];
    }
  }

  /**
   * Obtener un insumo específico por ID
   * @param {number} id - ID del insumo
   * @param {string|number|null} area - Área solicitada (string ej. 'BIOMEDICA' o ID numérico)
   * @returns {Promise<Object|null>} Datos del insumo o null si no se encuentra
   */
  async obtenerInsumo(id, area = null) {
    if (!id) {
      console.error('Se requiere el ID del insumo');
      return null;
    }

    try {
      const params = { id };

      if (area !== null && area !== undefined && area !== '') {
        if (!Number.isNaN(Number(area)) && Number.isFinite(Number(area))) {
          params.areaId = Number(area);
        } else {
          params.area = area;
        }
      }

      const response = await axios.get('/insumos/obtener', { params });

      if (response.data.success) {
        return response.data.insumo;
      } else {
        console.error('Error al obtener insumo:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('Error al obtener insumo:', error);
      return null;
    }
  }

  /**
   * Listar todos los insumos con paginación
   * @param {number} offset - Inicio de la paginación (default: 0)
   * @param {number} max - Número máximo de resultados (default: 20)
   * @param {string|number|null} area - Área solicitada (string ej. 'BIOMEDICA' o ID numérico)
   * @returns {Promise<Object>} Objeto con resultados y metadatos de paginación
   */
  async listarInsumos(offset = 0, max = 20, area = null) {
    try {
      const params = {
        offset: offset,
        max: max
      };

      if (area !== null && area !== undefined && area !== '') {
        if (!Number.isNaN(Number(area)) && Number.isFinite(Number(area))) {
          params.areaId = Number(area);
        } else {
          params.area = area;
        }
      }

      const response = await axios.get('/insumos/listar', { params });

      if (response.data.success) {
        return {
          resultados: response.data.resultados,
          total: response.data.total,
          offset: response.data.offset,
          max: response.data.max
        };
      } else {
        console.error('Error al listar insumos:', response.data.message);
        return {
          resultados: [],
          total: 0,
          offset: 0,
          max: 0
        };
      }
    } catch (error) {
      console.error('Error al listar insumos:', error);
      return {
        resultados: [],
        total: 0,
        offset: 0,
        max: 0
      };
    }
  }

  /**
   * Formatear un insumo para mostrar en el autocompletado
   * @param {Object} insumo - Objeto insumo
   * @returns {string} Texto formateado
   */
  formatearInsumo(insumo) {
    if (!insumo) return '';
    
    // Si ya tiene textoCompleto, usarlo
    if (insumo.textoCompleto) {
      return insumo.textoCompleto;
    }
    
    // Construir texto completo
    let texto = '';
    if (insumo.clave) texto += `${insumo.clave} - `;
    texto += insumo.concepto || '';
    if (insumo.presentacion) texto += ` (${insumo.presentacion})`;
    
    return texto;
  }

  /**
   * Formatear un insumo para mostrar detalles adicionales
   * @param {Object} insumo - Objeto insumo
   * @returns {string} Detalles formateados
   */
  formatearDetalles(insumo) {
    if (!insumo) return '';
    
    let detalles = [];
    
    if (insumo.clave) detalles.push(`Clave: ${insumo.clave}`);
    if (insumo.concepto) detalles.push(`Concepto: ${insumo.concepto}`);
    if (insumo.presentacion) detalles.push(`Presentación: ${insumo.presentacion}`);
    if (insumo.observaciones) detalles.push(`Observaciones: ${insumo.observaciones}`);
    
    return detalles.join(' | ');
  }
}

// Exportar una instancia única del servicio (singleton)
export default new InsumosService();

